import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  createMultipleSlots,
  CreateMultipleSlotsPayload,
} from "../../api/slots";
import { getClinicsByUser } from "../../api/clinic";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";

const SlotsBulkCreate: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const fetchedClinicsRef = useRef(false);
  const [startTime, setStartTime] = React.useState<Dayjs | null>(null);
  const [endTime, setEndTime] = React.useState<Dayjs | null>(null);
  const [formData, setFormData] = useState<CreateMultipleSlotsPayload>({
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    clinicId: "",
    status: true,
    day: [],
  });
  const [errors, setErrors] = useState<{
    startTime?: string;
    endTime?: string;
    startDate?: string;
    endDate?: string;
    clinicId?: string;
  }>({});

  useEffect(() => {
    if (fetchedClinicsRef.current) return;
    fetchedClinicsRef.current = true;
    const fetchClinics = async () => {
      setClinicsLoading(true);
      try {
        const res = await getClinicsByUser();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c: any) => ({
            id: c.id || c._id || c.clinicId || c.value || "",
            name: c.name || c.clinicName || c.label || "",
          }))
          .filter((c) => c.id && c.name);
        setClinics(normalized);
      } catch {
        setClinics([]);
      } finally {
        setClinicsLoading(false);
      }
    };
    fetchClinics();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleDay = (d: string) => {
    setFormData((prev) => {
      const exists = prev.day.includes(d);
      return {
        ...prev,
        day: exists ? prev.day.filter((x) => x !== d) : [...prev.day, d],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (!formData.startTime) nextErrors.startTime = "Start time is required";
    if (!formData.endTime) nextErrors.endTime = "End time is required";
    if (!formData.startDate) nextErrors.startDate = "Start date is required";
    if (!formData.endDate) nextErrors.endDate = "End date is required";
    if (!formData.clinicId) nextErrors.clinicId = "Clinic is required";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setLoading(true);
    try {
      await createMultipleSlots(formData);
      toast.success("Bulk slots created successfully");
      navigate("/slots");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to bulk create slots"
      );
    } finally {
      setLoading(false);
    }
  };

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />} {clinicsLoading && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Bulk Create Slots
                </h2>
                <button
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Start Time <span className="text-red-500">*</span>
                  </label>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={startTime}
                      onChange={(newValue) => {
                        setStartTime(newValue);
                        // convert Dayjs -> hh:mm AM/PM string
                        setFormData((prev) => ({
                          ...prev,
                          startTime: newValue ? newValue.format("hh:mm A") : "",
                        }));
                        if (errors.startTime)
                          setErrors((prev) => ({
                            ...prev,
                            startTime: undefined,
                          }));
                      }}
                      ampm
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          fullWidth: true,
                          className: "mt-1 w-full p-1 border rounded-xl",
                          placeholder: "HH:MM AM/PM",
                          required: true,
                          title: "Format must be HH:MM AM/PM",
                          error: Boolean(errors.startTime),
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {errors.startTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.startTime}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    End Time <span className="text-red-500">*</span>
                  </label>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={endTime}
                      onChange={(newValue) => {
                        setEndTime(newValue);
                        setFormData((prev) => ({
                          ...prev,
                          endTime: newValue ? newValue.format("hh:mm A") : "",
                        }));
                        if (errors.endTime)
                          setErrors((prev) => ({
                            ...prev,
                            endTime: undefined,
                          }));
                      }}
                      ampm
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          fullWidth: true,
                          className: "mt-1 w-full p-1 border rounded-xl",
                          placeholder: "HH:MM AM/PM",
                          required: true,
                          title: "Format must be HH:MM AM/PM",
                          error: Boolean(errors.endTime),
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {errors.endTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.endTime}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.startDate
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                    required
                  />
                  {errors.startDate && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.startDate}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.endDate
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                    required
                  />
                  {errors.endDate && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.endDate}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Clinic <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="clinicId"
                    value={formData.clinicId}
                    onChange={handleChange}
                    required
                    disabled={clinicsLoading}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.clinicId
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                  >
                    {clinicsLoading ? (
                      <option value="">Loading clinics...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Clinic
                        </option>
                        {clinics.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.clinicId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.clinicId}
                    </p>
                  )}
                </div>
                <div >
                  <label className="block text-sm font-medium text-gray-600">
                    Days
                  </label>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {days.map((d) => (
                      <label
                        key={d}
                        className="inline-flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.day.includes(d)}
                          onChange={() => toggleDay(d)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{d}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-xl   text-white   transition disabled:opacity-60"
                  >
                    {loading ? "Saving..." : "Create Slots"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default SlotsBulkCreate;
